import type { RouteMonitoring } from '@/pages/Business/pages/Monitoring/models';
import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models';
import { getAddressForShipping } from '@/pages/Business/pages/Shippings/utils/shippingAddress';
import { parseCoordFromMonitoring } from '../parse-monitoring-coord';

const isValidCoord = (lat: number, long: number): boolean =>
  Number.isFinite(lat) && Number.isFinite(long) && !(lat === 0 && long === 0);

export function resolveRoutePointLatLong(
  visit: RouteMonitoring['visits'][number],
  shipping: ShippingForRouting | undefined,
  enrichedShipping?: ShippingForRouting | undefined
): { lat: number; long: number } {
  const entityLat = visit.lat;
  const entityLong = visit.long;
  const hasEntityCoords =
    typeof entityLat === 'number' &&
    typeof entityLong === 'number' &&
    isValidCoord(entityLat, entityLong);

  if (visit.coordinatesChanged && hasEntityCoords) {
    return { lat: entityLat, long: entityLong };
  }

  // The route detail endpoint returns a single `visit.address` whose coordinates
  // can be collapsed to the operator base point for pickups. When we have the
  // real per-shipping data (`enrichedShipping`, fetched by number), use the
  // pickup-aware address (origin for pickups, destination otherwise) so each
  // point lands on its actual location.
  if (enrichedShipping) {
    const addr = getAddressForShipping(enrichedShipping);
    const addrLat = addr?.lat != null ? Number(addr.lat) : NaN;
    const addrLong = addr?.long != null ? Number(addr.long) : NaN;
    if (isValidCoord(addrLat, addrLong)) {
      return { lat: addrLat, long: addrLong };
    }
  }

  if (
    typeof entityLat === 'number' &&
    Number.isFinite(entityLat) &&
    typeof entityLong === 'number' &&
    Number.isFinite(entityLong)
  ) {
    return { lat: entityLat, long: entityLong };
  }

  const latFromVisit = parseCoordFromMonitoring(visit.address?.lat ?? null);
  const longFromVisit = parseCoordFromMonitoring(visit.address?.long ?? null);
  const addr = shipping ? getAddressForShipping(shipping) : undefined;
  const lat = latFromVisit ?? (addr?.lat != null ? Number(addr.lat) : 0);
  const long = longFromVisit ?? (addr?.long != null ? Number(addr.long) : 0);
  return { lat, long };
}
